<script>
import useSharedData from "@/use/SharedData.js";
import useValidation from "@/use/Validation.js";
import useTools from "@/use/Tools.js";
import {computed, onMounted, ref} from "vue";
import {useDebtStore} from "@/stores/Debt.js";
import useSharedMethod from "@/use/SharedMethod.js";
import axios from "axios";
import WCodeGeneration from "@/components/WCodeGeneration.vue";

export default {
    components: {WCodeGeneration},
    props: {
        addAction: Boolean,
        updateAction: Boolean,
        deleteAction: Boolean,
        viewAction: Boolean,
        pay_day_advance: Boolean,
    },

    setup(props) {
        const {mxRules} = useValidation();
        const {loading, dialog, overlay, inputColor, backgroundColor} = useSharedData();
        const {$route, $refs} = useTools();
        const {getBack} = useSharedMethod();

        const debtStore = useDebtStore();

        const readonly = computed(() => {
            return props.viewAction || props.deleteAction;
        });

        const debt = ref({
            duration: props.pay_day_advance ? 1 : null,
            type: props.pay_day_advance ? "PAY_DAY_ADVANCE" : "NORMAL",
        });

        function findById(id) {
            loading.value = true;
            axios.get(`/debts/${id}`).then((response) => {
                debt.value = response.data;
            }).finally(() => {
                loading.value = false;
            });
        }

        function save() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.post('/debts', debt.value).then((response) => {
                    debtStore.addDebt(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function update() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.put('/debts', debt.value).then((response) => {
                    debtStore.updateDebt(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function remove() {
            loading.value = true;
            axios.delete(`/debts/${debt.value.id}`).then(() => {
                debtStore.deleteDebt(debt.value);
                getBack();
            }).finally(() => {
                loading.value = false;
            });
        }

        onMounted(() => {
            if (!props.addAction) {
                findById($route.params.id);
            }
        });

        return {
            debt,
            inputColor,
            backgroundColor,
            mxRules,
            readonly,
            loading,
            dialog,
            overlay,
            save,
            update,
            remove,
            getBack,
        };
    },
};
</script>

<template>
    <v-dialog
        v-model="dialog"
        persistent
        max-width="900"
        scrollable
    >
        <v-card>
            <w-dialog-toolbar
                :add-action="addAction"
                :update-action="updateAction"
                :delete-action="deleteAction"
                :view-action="viewAction"
            ></w-dialog-toolbar>
            <w-overlay
                :loading="loading"
                :overlay="overlay"
                :delete-action="deleteAction"
                @remove="remove"
                @cancel="overlay = false"
            ></w-overlay>

            <v-card-text>
                <v-form ref="form" lazy-validation>
                    <v-container fluid>
                        <w-fieldset :title="$t('common.details')">
                            <v-row>
                                <v-col cols="6">
                                    <w-label :label="$t('common.code')" required/>
                                    <w-code-generation
                                        v-model="debt.code"
                                        :entity="'DEBT'"
                                        :load="addAction"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    ></w-code-generation>
                                </v-col>
                                <v-col cols="6">
                                    <w-label :label="$t('module.worker')" required/>
                                    <str-worker-autocomplete
                                        v-model="debt.workerId"
                                        action="SAVE_DEBT"
                                        :rules="[mxRules.required]"
                                        :readonly="readonly"
                                    />
                                </v-col>
                                <v-col cols="6">
                                    <w-label :label="$t('common.amount')" required/>
                                    <w-text-field
                                        v-model="debt.price"
                                        :readonly="readonly"
                                        type="number"
                                        :rules="[mxRules.required, mxRules.integer]"
                                    />

                                </v-col>
                                <v-col cols="6">
                                    <w-label :label="`${$t('common.duration')} (${$t('common.month')})`" required/>
                                    <w-text-field
                                        v-model="debt.duration"
                                        :readonly="readonly || pay_day_advance"
                                        :rules="[mxRules.required, mxRules.integer]"
                                        type="number"
                                    />
                                </v-col>
                                <v-col cols="6">
                                    <w-label :label="$t('common.date')" required/>
                                    <w-date-picker
                                        v-model="debt.date"
                                        :rules="[mxRules.required]"
                                        :readonly="readonly"
                                    />
                                </v-col>
                                <v-col cols="6">
                                    <w-label :label="$t('debts.start_at')" required/>
                                    <w-month-picker
                                        v-model="debt.startAt"
                                        :rules="[mxRules.required]"
                                        :readonly="readonly"
                                    />
                                </v-col>
                                <v-col cols="12">
                                    <w-label :label="$t('common.observation')"/>
                                    <w-simple-text-area
                                        v-model="debt.observation"
                                        :readonly="readonly"
                                    />
                                </v-col>
                            </v-row>
                        </w-fieldset>
                    </v-container>
                </v-form>
            </v-card-text>
            <v-divider/>
            <v-card-actions>
                <v-spacer/>
                <w-btn-close @press="getBack"/>
                <w-btn-add
                    :show="addAction"
                    :loading="loading"
                    @press="save"
                />
                <w-btn-update
                    :show="updateAction"
                    :loading="loading"
                    @press="update"
                />
                <w-btn-delete
                    :show="deleteAction"
                    @press="overlay = true"
                />
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>